import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Button, Card, CardHeader, CardBody, Input, Badge, LoadingSpinner } from '../components/ui';
import { authService } from '../services/auth';
import { vaultService, type VaultPrincipal } from '../services/vault.service';
import { showToast } from '../utils/toast';

// Color tokens used throughout match the rest of the admin app (see JuniperData.tsx):
//   heading:  text-xtnd-dark-800 dark:text-white
//   subhead:  text-xtnd-dark-700 dark:text-xtnd-dark-200
//   body:     text-xtnd-dark-600 dark:text-xtnd-dark-300
//   muted:    text-xtnd-dark-500 dark:text-xtnd-dark-400

export function PrivilegedAccess() {
  const user = authService.getCurrentUser();
  const isCoraledge = !!user?.email?.toLowerCase().endsWith('@thecoraledge.com');

  return (
    <div className="space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-bold text-xtnd-dark-800 dark:text-white mb-2">
          Privileged Access
        </h1>
        <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300">
          Request the bot account password by email, or view the MFA QR for any tenant you're
          authorized on. The password and account email are never shown together — the password
          arrives via mail; the QR appears here.
        </p>
      </header>

      <MyAccessSection />

      {isCoraledge && <PrincipalsAdminSection />}
    </div>
  );
}

// ── My access ─────────────────────────────────────────────────────────────

function MyAccessSection() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['vault', 'my-tenants'],
    queryFn: () => vaultService.myTenants(),
    staleTime: 30_000,
  });

  if (isLoading) return <LoadingSpinner />;
  if (error)
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-red-600 dark:text-red-400">
            Failed to load access list: {(error as Error).message}
          </p>
        </CardBody>
      </Card>
    );
  const tenants = data ?? [];

  return (
    <section>
      <h2 className="text-lg font-semibold text-xtnd-dark-800 dark:text-white mb-3">My access</h2>
      {tenants.length === 0 ? (
        <Card>
          <CardBody>
            <p className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300">
              You aren't authorized for any tenants. Ask a Coral Edge admin to add you.
            </p>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-3">
          {tenants.map((row) => (
            <TenantAccessCard key={row.tenantId} principal={row} />
          ))}
        </div>
      )}
    </section>
  );
}

function TenantAccessCard({ principal }: { principal: VaultPrincipal }) {
  const [mfaUrl, setMfaUrl] = useState<string | null>(null);
  const [hasOpenCheckout, setHasOpenCheckout] = useState(false);
  const qc = useQueryClient();

  const checkout = useMutation({
    mutationFn: () => vaultService.checkoutPassword(principal.tenantId),
    onSuccess: () => {
      setHasOpenCheckout(true);
      qc.invalidateQueries({ queryKey: ['vault', 'my-tenants'] });
      showToast.success(
        `Password email dispatched to ${principal.email}. Check your inbox, then click "Done — release lease" when finished so the password can rotate.`,
      );
    },
    onError: (err) => showToast.error(`Checkout failed: ${(err as Error).message}`),
  });

  const release = useMutation({
    mutationFn: () => vaultService.releaseCredential(principal.tenantId),
    onSuccess: () => {
      setHasOpenCheckout(false);
      qc.invalidateQueries({ queryKey: ['vault', 'my-tenants'] });
      showToast.success('Lease released. Next checkout will rotate the password.');
    },
    onError: (err) => showToast.error(`Release failed: ${(err as Error).message}`),
  });

  const fetchMfa = useMutation({
    mutationFn: () => vaultService.getMfaOtpauth(principal.tenantId),
    onSuccess: (res) => setMfaUrl(res.otpauth),
    onError: (err) => showToast.error(`MFA fetch failed: ${(err as Error).message}`),
  });

  const lastCheckout = principal.lastCheckoutAtEpoch
    ? new Date(principal.lastCheckoutAtEpoch * 1000).toLocaleString()
    : 'Never';

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-medium text-xtnd-dark-800 dark:text-white">
              Tenant {principal.tenantId}
            </div>
            <div className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400">
              Last checkout: {lastCheckout}
            </div>
          </div>
          <Badge variant={principal.enabled ? 'success' : 'destructive'}>
            {principal.enabled ? 'enabled' : 'disabled'}
          </Badge>
        </div>
      </CardHeader>
      <CardBody>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => checkout.mutate()}
            disabled={!principal.enabled || checkout.isPending}
          >
            {checkout.isPending ? 'Sending…' : 'Email me the password'}
          </Button>
          {hasOpenCheckout && (
            <Button
              variant="outline"
              onClick={() => release.mutate()}
              disabled={release.isPending}
            >
              {release.isPending ? 'Releasing…' : 'Done — release lease'}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => (mfaUrl ? setMfaUrl(null) : fetchMfa.mutate())}
            disabled={!principal.enabled || fetchMfa.isPending}
          >
            {mfaUrl ? 'Hide MFA QR' : fetchMfa.isPending ? 'Loading…' : 'Show MFA QR'}
          </Button>
        </div>
        <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400 mt-3">
          A checkout creates a 60-minute lease. While the lease is active, all
          authorized users who check out get the same password. Click <em>Release
          lease</em> when done — the next checkout after everyone releases (or
          the lease expires) rotates the password.
        </p>

        {mfaUrl && (
          <div className="mt-4 inline-block bg-white p-4 rounded border border-xtnd-dark-200">
            <QRCodeSVG value={mfaUrl} size={192} level="M" />
            <div className="text-xs text-xtnd-dark-600 mt-2 max-w-[192px]">
              {'Scan with your authenticator app. The label is "Vault — {tenant id}" so multiple bots can be told apart; no account email is disclosed.'}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

// ── Coral Edge admin: principals CRUD ────────────────────────────────────

function PrincipalsAdminSection() {
  const [tenantId, setTenantId] = useState('');
  const [emailToAdd, setEmailToAdd] = useState('');
  const [notes, setNotes] = useState('');
  const qc = useQueryClient();

  const enabled = tenantId.trim().length > 0;

  const list = useQuery({
    enabled,
    queryKey: ['vault', 'principals', tenantId],
    queryFn: () => vaultService.listPrincipals(tenantId.trim()),
  });

  const principals = list.data ?? [];

  const add = useMutation({
    mutationFn: () =>
      vaultService.addPrincipal(tenantId.trim(), emailToAdd.trim(), notes.trim() || undefined),
    onSuccess: () => {
      setEmailToAdd('');
      setNotes('');
      qc.invalidateQueries({ queryKey: ['vault', 'principals', tenantId] });
      qc.invalidateQueries({ queryKey: ['vault', 'my-tenants'] });
    },
    onError: (err) => showToast.error(`Add failed: ${(err as Error).message}`),
  });

  const remove = useMutation({
    mutationFn: (email: string) => vaultService.removePrincipal(tenantId.trim(), email),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vault', 'principals', tenantId] });
      qc.invalidateQueries({ queryKey: ['vault', 'my-tenants'] });
    },
    onError: (err) => showToast.error(`Remove failed: ${(err as Error).message}`),
  });

  return (
    <section>
      <h2 className="text-lg font-semibold text-xtnd-dark-800 dark:text-white mb-3">
        Manage principals (Coral Edge only)
      </h2>
      <Card>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              placeholder="Tenant ID (e.g. 0000000001)"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
            />
          </div>

          {enabled && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <Input
                  placeholder="Email to authorize"
                  type="email"
                  value={emailToAdd}
                  onChange={(e) => setEmailToAdd(e.target.value)}
                />
                <Input
                  placeholder="Notes (optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
                <Button
                  onClick={() => add.mutate()}
                  disabled={!emailToAdd.trim() || add.isPending}
                >
                  {add.isPending ? 'Adding…' : 'Add principal'}
                </Button>
              </div>

              {list.isLoading ? (
                <LoadingSpinner />
              ) : (
                <ul className="divide-y divide-xtnd-dark-200 dark:divide-xtnd-dark-700 border border-xtnd-dark-200 dark:border-xtnd-dark-700 rounded">
                  {principals.length === 0 ? (
                    <li className="p-3 text-sm text-xtnd-dark-500 dark:text-xtnd-dark-400">
                      No principals yet for this tenant.
                    </li>
                  ) : (
                    principals.map((p) => (
                      <li key={p.email} className="flex items-center justify-between p-3">
                        <div>
                          <div className="font-medium text-xtnd-dark-800 dark:text-white">
                            {p.email}
                          </div>
                          <div className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-400">
                            Added by {p.addedBy} •{' '}
                            {new Date(p.addedAtEpoch * 1000).toLocaleString()}
                            {p.notes && <> • {p.notes}</>}
                          </div>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => remove.mutate(p.email)}
                          disabled={remove.isPending}
                        >
                          Revoke
                        </Button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </section>
  );
}
